import React, { FC, useContext, useMemo } from 'react';
import { observer } from 'mobx-react';
import { NotificationIcon, NotificationIconDark, CopyIcon, Plus } from '@components/icons';
import { RootStoreContext } from '@stores/rootStore';
import { useTranslation } from 'react-i18next';
import { sendGaEvent } from '@services/utils';
import { toast } from 'react-toastify';
import { LFButton, LFButtonColor, LFButtonType } from '@components/LFButton';
import { useRouter } from 'next/navigation';
import { SidebarHeader } from './_components/SidebarHeader';
import { SidebarElement, sidebarElements } from './_components/SidebarElement';
import { SidebarListElement } from './_components/SidebarListElement';
import { SidebarNotificationElement } from './_components/SidebarNotificationElement';
export type ActiveTabs = 'notifications' | 'overview' | 'cropzoneBoard' | 'management' | 'settings' | 'planning' | 'traceability' | 'supplyChain' | 'monitoring' | 'activities' | 'logout' | 'copy' | 'preferences' | 'passkeys';
export const Sidebar: FC<{
  collapsed: boolean;
  sidebarActiveTab: ActiveTabs;
  toggleSidebar: () => void;
}> = observer(({
  collapsed,
  sidebarActiveTab,
  toggleSidebar
}) => {
  const {
    uiStore,
    userStore
  } = useContext(RootStoreContext);
  const router = useRouter();
  const {
    t
  } = useTranslation();
  const hasPlanningFeature = uiStore.currentOrganization?.hasPlanningFeature;
  const hasTraceabilityFeature = uiStore.currentOrganization?.hasTraceabilityFeature;
  const hasSupplyChainFeature = uiStore.currentOrganization?.hasSupplyChainFeature;
  const screenSize = getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-md');
  const tailwindBreakpoint = parseInt(screenSize, 10);
  const copyUrlToClipboard = () => {
    const id = uiStore.currentOrganization?.id;
    if (!id) return;
    const hasQuery = location.href.includes('?');
    const text = `${location.href}${hasQuery ? '&' : '?'}currentOrgId=${id}`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`COPIED: ${text}`);
    });
  };
  const onElementClick = (element: {
    id: ActiveTabs;
  }) => {
    sendGaEvent(`click_${element.id}`, {
      action: 'click'
    });
    if (window.innerWidth < tailwindBreakpoint) {
      uiStore.sidebar.toggleSidebar();
    }
    uiStore.lfmap.resetBboxes();
  };
  const visibleSidebarElements = useMemo(() => sidebarElements.filter(element => (element.id !== 'planning' || hasPlanningFeature) && (element.id !== 'traceability' || hasTraceabilityFeature) && (element.id !== 'supplyChain' || hasSupplyChainFeature)), [sidebarElements, hasPlanningFeature, hasTraceabilityFeature, hasSupplyChainFeature]);
  const onCropzoneCreateClick = () => {
    router.push('/cropzones/new');
    uiStore.lfmap.resetBboxes();
  };
  const onQuickCropzoneClick = () => {
    router.push('/cropzones/drafts/new');
    sendGaEvent('create_quick_cropzone', {
      event_category: 'quick_cropzone',
      action: 'click'
    });
    uiStore.lfmap.resetBboxes();
  };
  return <div>
      <div onClick={toggleSidebar} className={`
          ${collapsed ? '' : 'fixed inset-0 z-45 h-full w-full overflow-y-auto bg-black/90 duration-300'}
          md:hidden
        `} />
      <div className={`
          ${collapsed ? 'hidden' : 'bg-smoke w-min-fit fixed flex flex-col'}
          md:bg-smoke md:relative md:flex md:w-auto md:min-w-max md:flex-col md:pt-0
          top-0 left-0 z-45 h-screen px-4 pt-4 transition-all duration-100
          print:hidden
        `}>
        <SidebarHeader collapsed={collapsed} onClick={toggleSidebar} />

      {/* For the moment, hiding Notifications from the Sidebar
              <div className="border-silver mb-2 w-full border border-t-0 px-4" />
               <SidebarNotificationElement
                element={{
                  id: 'notifications',
                  icon: (active) =>
                    active ? <NotificationIconDark /> : <NotificationIcon />,
                  url: '/notifications',
                }}
                collapsed={collapsed}
                onClick={onElementClick}
                sidebarActiveTab={sidebarActiveTab}
              /> 
       */}

        <div className="border-silver mt-2 w-full border border-t-0 px-4" />

        <div className="flex h-full w-full flex-col overflow-hidden">
          <div className="h-full overflow-y-auto">
            <ul>
              {visibleSidebarElements.map(element => <SidebarListElement key={`sidebar.${element.id}`} element={element} collapsed={collapsed} active={element.id === sidebarActiveTab} onClick={onElementClick} />)}
            </ul>
            <div className={`
                my-8 flex h-fit justify-center px-8
                md:mt-12 md:px-12
                ${collapsed ? 'hidden' : ''}
                flex-col gap-y-3
              `}>
              <LFButton disabled={!uiStore.isReady} color={LFButtonColor.MAIN} type={LFButtonType.PRIMARY} onClick={onCropzoneCreateClick} className={`
                  px-8
                  md:px-12
                `} testId="cropzone-create-button">
                <Plus />
                {t('createCropzone')}
              </LFButton>

              <LFButton disabled={!uiStore.isReady} color={LFButtonColor.INFORMATION} type={LFButtonType.PRIMARY} onClick={onQuickCropzoneClick} className={`
                  mt-3 block px-8
                  md:px-12
                  lg:hidden
                `}>
                <Plus />
                {t('quickCropzone')}
              </LFButton>
            </div>
          </div>
        </div>

        <div className="mb-20 ">
          {(uiStore.isStaging || userStore.isLF) && <>
              <div className={`
                  ${collapsed ? 'px-0' : 'px-8'}
                  text-grey flex h-24 w-full grow gap-x-4
                  hover:text-secondary-300
                `}>
                <button onClick={copyUrlToClipboard}>
                  <SidebarElement element={{
                id: 'copy',
                icon: () => <CopyIcon />
              }} collapsed={collapsed} customStyles="flex" />
                </button>
                <p className={`
                    ${collapsed ? 'md:hidden' : ''}
                    font-thinner text-negative-300 content-center items-center text-sm
                  `}>
                  (lf-only)
                </p>
              </div>
            </>}
        </div>
      </div>
    </div>;
});